import React, {Suspense} from 'react';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import Header from '@di-shared/features/common/Header';
import HeaderCloseButton from '@di-shared/features/common/HeaderCloseButton';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {useSuspenseQuery} from '@tanstack/react-query';

interface MileageFisrtRegisterProps {
  vin: string;
}

function MileageFirstRegister({vin}: MileageFisrtRegisterProps) {
  return (
    <Suspense fallback={<MileageFirstRegisterLoading />}>
      <Inner {...{vin}} />
    </Suspense>
  );
}

function Inner({vin}: MileageFisrtRegisterProps) {
  const {t} = useInferableTranslation();
  const {openAlert, backOrClose} = useCcWebviewUtilsHelper();
  const navigate = useNavigate();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.ecoMileageController.getInfoLink)],
    queryFn: () =>
      diApi.ecoMileageController.getInfoLink({
        vin,
      }),
  });
  const date = parseDateWithFormat(data.data.rtn_data.drvng_dstnc_endde);

  const [onRegister] = usePendingHandler(
    async () => {
      await diApi.ecoMileageController.updateVehicleLink({vin, requestBody: {vin}});
      navigate(ROUTE_PATHS.MILEAGE_ECO.getFullPath(vin), {replace: true});
    },
    async () => {
      await openAlert({
        title: t('T30390'),
        message: t('T30391_$tel', {tel: import.meta.env.VITE_EMERGENCY_TEL_BRAND}),
      });
      backOrClose();
    }
  );

  return (
    <>
      <Header right={<HeaderCloseButton onClick={() => backOrClose()} />}>
        <h1 className="title_xsmall_b nav_center_title">{t('T10122')}</h1>
      </Header>
      <main
        id="content"
        className="content page_eco_mileage"
      >
        <section className="section section_mileage_submit">
          <div className="section_heading type_main">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T30618_$year', {year: new Date().getFullYear()})}
            </h2>
          </div>
          <div className="mileage_submit_box type_dot">
            <p className="mileage_submit_desc">{t('T30619_$date', {date: date ? CldrDatetimeFormatter.format(date, 'MMMd') : '-'})}</p>
            <p className="mileage_submit_desc">{t('T30620')}</p>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={onRegister}
        >
          {t('T30621')}
        </button>
      </div>
    </>
  );
}

export function MileageFirstRegisterLoading() {
  const {backOrClose} = useCcWebviewUtilsHelper();
  const {t} = useInferableTranslation();
  return (
    <>
      <Header
        right={
          <HeaderCloseButton
            onClick={() => {
              backOrClose();
            }}
          />
        }
      >
        <h1 className="title_xsmall_b nav_center_title">{t('T10122')}</h1>
      </Header>
      <main
        id="content"
        className="content page_eco_mileage"
      >
        <section className="section section_mileage_submit">
          <div className="section_heading type_main">
            <h2 className="section_heading_title skeleton">{t('T10033')}</h2>
          </div>
          <div
            className="mileage_submit_box type_dot skeleton"
            style={{height: 80}}
          ></div>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          disabled
        >
          {t('T30621')}
        </button>
      </div>
    </>
  );
}

export default MileageFirstRegister;
